
import React, { useState, useEffect, useRef } from 'react';
import { Report } from '../types';
import { MapPin, Save, Building2, Loader2, MousePointer2 } from 'lucide-react';
import { Button } from './Button';
import L from 'leaflet';

interface Props {
  report: Report;
  onUpdate: (report: Report) => void;
}

export const ProjectLocationView: React.FC<Props> = ({ report, onUpdate }) => {
  const meta = report.metadata;
  const globalData = meta.globalData;
  
  // States
  const [map, setMap] = useState<L.Map | null>(null);
  const [marker, setMarker] = useState<L.Marker | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [mapLayer, setMapLayer] = useState<'roadmap' | 'satellite'>('roadmap');
  
  // Pending changes before save
  const [tempCoords, setTempCoords] = useState<{lat: number, lng: number} | null>(globalData?.locationCoordinates || null);
  const [tempAddress, setTempAddress] = useState<string>(meta.projectAddress || '');

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  // Initialize Map
  useEffect(() => {
    if (!mapContainerRef.current || map) return;

    // Initial position: Coordinates from DB, or geocode current address, or default to DE center
    const startPos: [number, number] = tempCoords ? [tempCoords.lat, tempCoords.lng] : [51.1657, 10.4515];
    const initialZoom = tempCoords ? 16 : 6;

    const leafletMap = L.map(mapContainerRef.current, {
      zoomControl: false,
      attributionControl: false
    }).setView(startPos, initialZoom);

    L.control.zoom({ position: 'bottomright' }).addTo(leafletMap);
    
    // Set layer
    const layerUrl = mapLayer === 'roadmap' 
        ? 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'
        : 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}';
    
    tileLayerRef.current = L.tileLayer(layerUrl, { maxZoom: 20 }).addTo(leafletMap);
    
    setMap(leafletMap);

    // Initial Marker
    const icon = L.divIcon({
      className: 'bg-transparent',
      html: `<div style="background-color: #2563eb; width: 32px; height: 32px; border-radius: 50% 50% 0 50%; transform: rotate(45deg); border: 3px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;"><div style="width: 10px; height: 10px; background-color: white; border-radius: 50%;"></div></div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32]
    });

    const newMarker = L.marker(startPos, { icon, draggable: true }).addTo(leafletMap);

    newMarker.on('dragend', async (e) => {
        const { lat, lng } = e.target.getLatLng();
        setTempCoords({ lat, lng });
        reverseGeocode(lat, lng);
    });

    // Allow setting the location by clicking anywhere on the map, not only by
    // dragging the pin (dragging is fiddly, especially on touch devices).
    leafletMap.on('click', (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        newMarker.setLatLng([lat, lng]);
        setTempCoords({ lat, lng });
        reverseGeocode(lat, lng);
    });

    setMarker(newMarker);

    // If the project has no stored coordinates yet, geocode its existing address
    // so the map opens AT the project location instead of the centre of Germany.
    // (The original code promised this but never implemented it.)
    if (!tempCoords && (meta.projectAddress || '').trim().length >= 3) {
        (async () => {
            try {
                const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(meta.projectAddress)}&limit=1`;
                const res = await fetch(url, { headers: { 'User-Agent': 'SiGeKo-App-Locator' } });
                const data = await res.json();
                if (data && data.length > 0) {
                    const lat = parseFloat(data[0].lat);
                    const lng = parseFloat(data[0].lon);
                    setTempCoords({ lat, lng });
                    leafletMap.setView([lat, lng], 16);
                    newMarker.setLatLng([lat, lng]);
                }
            } catch (err) {
                console.error('Initial address geocode failed', err);
            }
        })();
    }

    // FIX: Map invalidate size to fix rendering issues inside modals/tabs
    setTimeout(() => {
        leafletMap.invalidateSize();
    }, 200);

    return () => {
      leafletMap.remove();
    };
  }, []);

  // Update Tile Layer
  useEffect(() => {
      if (!map || !tileLayerRef.current) return;
      const layerUrl = mapLayer === 'roadmap' 
          ? 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'
          : 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}';
      tileLayerRef.current.setUrl(layerUrl);
  }, [mapLayer, map]);

  const reverseGeocode = async (lat: number, lng: number) => {
      setIsGeocoding(true);
      try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`;
          const res = await fetch(url, { headers: { 'User-Agent': 'SiGeKo-App-Locator' } });
          const data = await res.json();
          
          if (data && data.address) {
              const addr = data.address;
              const street = addr.road || addr.pedestrian || addr.path || '';
              const houseNum = addr.house_number ? ` ${addr.house_number}` : '';
              const city = addr.city || addr.town || addr.village || addr.suburb || '';
              const zip = addr.postcode || '';
              
              let formatted = '';
              if (street) {
                  formatted = `${street}${houseNum}`;
                  if (zip || city) formatted += ', ';
              }
              if (zip || city) {
                  formatted += `${zip} ${city}`.trim();
              }
              
              setTempAddress(formatted);
          }
      } catch (e) {
          console.error("Reverse geocoding failed", e);
      } finally {
          setIsGeocoding(false);
      }
  };

  const handleSaveLocation = async () => {
      if (!tempCoords) return;
      setIsSaving(true);
      
      const updatedReport = {
          ...report,
          metadata: {
              ...meta,
              projectAddress: tempAddress,
              globalData: {
                  ...globalData,
                  locationCoordinates: tempCoords
              }
          }
      };

      try {
          onUpdate(updatedReport);
          alert("Standort und Adresse wurden erfolgreich im Projekt aktualisiert.");
      } catch {
          alert("Fehler beim Speichern.");
      } finally {
          setIsSaving(false);
      }
  };

  const jumpToAddress = async () => {
      if (!tempAddress) return;
      setIsGeocoding(true);
      try {
          const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(tempAddress)}&limit=1`;
          const res = await fetch(url, { headers: { 'User-Agent': 'SiGeKo-App-Locator' } });
          const data = await res.json();
          if (data && data.length > 0) {
              const lat = parseFloat(data[0].lat);
              const lng = parseFloat(data[0].lon);
              setTempCoords({ lat, lng });
              if (map && marker) {
                  map.setView([lat, lng], 17);
                  marker.setLatLng([lat, lng]);
              }
          }
      } catch {
          alert("Adresse konnte nicht gefunden werden.");
      } finally {
          setIsGeocoding(false);
      }
  };

  return (
    <div data-glass-view="projekt-standort" className="max-w-7xl mx-auto p-4 md:p-6 animate-fade-in space-y-6 pb-20 h-full flex flex-col">
       
       <div data-glass="kopf" className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 shrink-0">
          <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-2">
                 <MapPin className="w-6 h-6 text-blue-600" />
                 Projekt-Standort definieren
              </h2>
              <div className="flex items-center gap-3">
                  <div className={`flex-1 relative group`}>
                      <input 
                        type="text" 
                        value={tempAddress} 
                        onChange={(e) => setTempAddress(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-medium"
                        placeholder="Adresse eingeben oder Pin auf Karte ziehen..."
                      />
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      {isGeocoding && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />}
                  </div>
                  <Button variant="secondary" onClick={jumpToAddress} disabled={isGeocoding || !tempAddress} className="whitespace-nowrap px-4">
                      Suchen
                  </Button>
              </div>
          </div>
          
          <div className="flex gap-3 w-full lg:w-auto shrink-0">
             <div data-glass="umschalter" className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                 <button 
                    onClick={() => setMapLayer('roadmap')} 
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-all ${mapLayer === 'roadmap' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                 >
                     Karte
                 </button>
                 <button 
                    onClick={() => setMapLayer('satellite')} 
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-all ${mapLayer === 'satellite' ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                 >
                     Satellit
                 </button>
             </div>
             <Button 
                variant="primary" 
                onClick={handleSaveLocation} 
                disabled={isSaving || isGeocoding}
                className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-100 min-w-[200px]"
                icon={isSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
             >
                {isSaving ? 'Speichert...' : 'Standort übernehmen'}
             </Button>
          </div>
       </div>

       {/* Map Viewport - Added full height flexibility */}
       <div className="flex-1 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden relative flex flex-col min-h-[400px] h-full w-full">
          <div ref={mapContainerRef} className="flex-1 w-full h-full z-0 cursor-crosshair absolute inset-0" />
          
          {/* Map Overlay HUD */}
          <div className="absolute top-4 left-4 z-10 pointer-events-none flex flex-col gap-2">
              <div className="bg-slate-900/80 backdrop-blur-md text-white p-3 rounded-xl border border-white/10 shadow-2xl flex items-center gap-3">
                  <div className="bg-blue-500 p-2 rounded-lg">
                      <MousePointer2 className="w-4 h-4 text-white" />
                  </div>
                  <div>
                      <p className="text-[11px] font-bold uppercase tracking-widest text-blue-300">Interaktive Wahl</p>
                      <p className="text-xs font-medium">Pin gedrückt halten & verschieben</p>
                  </div>
              </div>
          </div>

          <div className="absolute bottom-4 left-4 z-10 pointer-events-none">
              <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl border border-slate-200 shadow-lg max-w-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></div>
                      <span className="text-[11px] font-black uppercase text-slate-400 tracking-widest">Live Koordinaten</span>
                  </div>
                  <div className="font-mono text-[11px] text-slate-600">
                      {tempCoords ? `${tempCoords.lat.toFixed(6)}, ${tempCoords.lng.toFixed(6)}` : 'Warte auf Position...'}
                  </div>
              </div>
          </div>
       </div>
    </div>
  );
};
